import { useFeatureGate } from '~/hooks/useFeatureGate';
import { useTenantAdmin } from '~/hooks/useTenantAdmin';

import { useHasFeedAuditBatch } from './use-feed-audit-list';

/**
 * True when the current user may run unlimited audits — super admins and org admins
 * (`useTenantAdmin`), on Google and Meta alike. Bypasses the one-audit-per-subsidiary
 * limit. Note `lily-restricted-uploads` still wins for the upload path (it can disable
 * Upload even for admins).
 */
export function useFeedAuditUnlimited(): boolean {
  return useTenantAdmin();
}

/**
 * True when the file-upload entry point must be disabled — the `lily-restricted-uploads`
 * flag, applied uniformly to any subsidiary. Only affects the upload path; select-
 * and-run is unaffected. Top weight over the admin bypass: when on, the upload path
 * is disabled even for admins.
 */
export function useFeedAuditUploadsRestricted(): boolean {
  return useFeatureGate('lily_restricted_uploads').isAllowed;
}

/**
 * True when the per-run 20-product cap is lifted — always for super admins and org
 * admins, otherwise the `lily-bypass-audit-cap` flag. Lets an audit run cover more than
 * 20 products across the Select & run picker, the Upload row count and catalog-v2 audits.
 */
export function useFeedAuditProductCapLifted(): boolean {
  const flagLifted = useFeatureGate('lily_bypass_audit_cap').isAllowed;
  const isAdmin = useTenantAdmin();
  return isAdmin || flagLifted;
}

/** Whether the active subsidiary may start a new Feed Audit, and why. */
export interface FeedAuditCreationPolicy {
  /** False once an audit exists and the user isn't an admin — Create is blocked. */
  canCreate: boolean;
  /** Whether the subsidiary already has at least one audit. */
  hasExistingAudit: boolean;
  /** Whether the user may run unlimited audits (super admin or org admin). */
  isUnlimited: boolean;
  /** Whether the existing-audit probe is still loading. */
  isLoading: boolean;
}

/**
 * Central Feed Audit creation gate: a subsidiary may run only ONE audit unless the
 * user is a super admin or org admin. Optimistic while loading (allows Create) so the
 * CTA never flashes disabled; the block engages once the probe resolves.
 */
export function useFeedAuditCreationPolicy(): FeedAuditCreationPolicy {
  const isUnlimited = useFeedAuditUnlimited();
  const { hasBatch, isLoading } = useHasFeedAuditBatch();
  const hasExistingAudit = hasBatch;
  const canCreate = isUnlimited || !hasExistingAudit;
  return { canCreate, hasExistingAudit, isUnlimited, isLoading };
}
